import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import {
  MagnifyingGlassIcon,
  PlayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  PhotoIcon,
  LinkIcon,
  CodeBracketIcon,
  DevicePhoneMobileIcon,
  BoltIcon,
  GlobeAltIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  HashtagIcon,
  ShareIcon,
  WrenchScrewdriverIcon,
  EyeIcon,
  TrashIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon,
  PlusIcon,
  ArrowTrendingUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import seoScannerApi, {
  SEOScanResult,
  SEOIssue,
  SEOSeverity,
  SEOCategory,
} from '../../services/api/seo-scanner.api';

type MainTab = 'scan' | 'history' | 'dashboard';
type ResultTab = 'overview' | 'seo' | 'performance' | 'issues';
type PageSpeedDevice = 'mobile' | 'desktop';

interface ScanHistoryItem {
  id: string;
  url: string;
  status: string;
  overall_score: number;
  critical_issues: number;
  warning_issues: number;
  info_issues: number;
  passed_checks: number;
  duration_ms: number;
  created_at: string;
  completed_at: string;
}

export default function SEOScannerPage() {
  const { t } = useTranslation('seo');

  // Main tabs
  const [mainTab, setMainTab] = useState<MainTab>('scan');

  // Scan state
  const [url, setUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<SEOScanResult | null>(null);
  const [resultTab, setResultTab] = useState<ResultTab>('overview');

  // Options
  const [checkRobots, setCheckRobots] = useState(true);
  const [checkSitemap, setCheckSitemap] = useState(true);

  // History state
  const [scans, setScans] = useState<ScanHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [totalScans, setTotalScans] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Dashboard state
  const [dashboard, setDashboard] = useState<{
    totalScans: number;
    averageScore: number;
    totalIssues: { critical: number; warnings: number; info: number };
    recentScans: Array<{ id: string; url: string; overall_score: number; created_at: string }>;
  } | null>(null);

  // Expanded issues
  const [expandedIssues, setExpandedIssues] = useState<Set<string>>(new Set());

  // Expanded sections in SEO tab
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['meta', 'headings']));

  // PageSpeed device toggle
  const [pageSpeedDevice, setPageSpeedDevice] = useState<PageSpeedDevice>('mobile');

  // Export state
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);

  // Load scan history
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await seoScannerApi.getScans({
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      });
      setScans(data.scans);
      setTotalScans(data.total);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [currentPage, pageSize]);

  // Load dashboard
  const loadDashboard = useCallback(async () => {
    try {
      const data = await seoScannerApi.getDashboard();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    }
  }, []);

  useEffect(() => {
    if (mainTab === 'history') {
      loadHistory();
    } else if (mainTab === 'dashboard') {
      loadDashboard();
    }
  }, [mainTab, loadHistory, loadDashboard]);

  // Get translated error message
  const getErrorMessage = (errorCode: string): string => {
    const translatedError = t(`errors.${errorCode}`, { defaultValue: '' });
    if (translatedError && translatedError !== `errors.${errorCode}`) {
      return translatedError;
    }
    // Fallback for backend errors
    if (errorCode.includes('Failed to fetch')) {
      return t('errors.FETCH_FAILED');
    }
    if (errorCode.includes('timeout') || errorCode.includes('Timeout')) {
      return t('errors.TIMEOUT');
    }
    return errorCode || t('scanFailed');
  };

  const handleScan = async () => {
    if (!url) {
      toast.error(t('enterUrlError'));
      return;
    }

    // Validate URL
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
    } catch (e) {
      toast.error(t('invalidUrlError'));
      return;
    }

    const targetUrl = url.startsWith('http') ? url : `https://${url}`;

    try {
      setScanning(true);
      setResult(null);
      const toastId = toast.loading(t('scanningMessage'));

      const scanResult = await seoScannerApi.scan(targetUrl, {
        checkRobots,
        checkSitemap,
      });

      console.log('[SEO Scanner] Scan completed:', scanResult);

      setResult(scanResult);
      setResultTab('overview');
      toast.dismiss(toastId);
      toast.success(`${t('scanComplete')} ${t('score')}: ${scanResult.score.overall}/100`);
    } catch (error: any) {
      console.error('[SEO Scanner] Error:', error);
      toast.dismiss();
      const errorMessage = getErrorMessage(error.message);
      toast.error(errorMessage);
    } finally {
      setScanning(false);
    }
  };

  const handleDeleteScan = async (id: string) => {
    if (!confirm(t('confirmDelete'))) return;

    try {
      await seoScannerApi.deleteScan(id);
      toast.success(t('deleteSuccess'));
      loadHistory();
    } catch (error) {
      console.error('Failed to delete scan:', error);
      toast.error('Failed to delete scan');
    }
  };

  const handleViewScan = async (id: string) => {
    try {
      const scanData = await seoScannerApi.getScan(id);
      setResult(scanData);
      setMainTab('scan');
      setResultTab('overview');
    } catch (error) {
      console.error('Failed to load scan:', error);
      toast.error('Failed to load scan');
    }
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    if (!result) return;

    setExporting(format);
    try {
      await seoScannerApi.exportScanDirect(result, format);
      toast.success(t('exportSuccess', { format: format.toUpperCase() }));
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error(t('exportFailed'));
    } finally {
      setExporting(null);
    }
  };

  const getRatingColor = (rating: 'good' | 'needs-improvement' | 'poor') => {
    switch (rating) {
      case 'good':
        return 'text-green-500';
      case 'needs-improvement':
        return 'text-orange-500';
      case 'poor':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getRatingBgColor = (rating: 'good' | 'needs-improvement' | 'poor') => {
    switch (rating) {
      case 'good':
        return 'bg-green-100 dark:bg-green-900/30';
      case 'needs-improvement':
        return 'bg-orange-100 dark:bg-orange-900/30';
      case 'poor':
        return 'bg-red-100 dark:bg-red-900/30';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30';
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getSeverityIcon = (severity: SEOSeverity) => {
    switch (severity) {
      case 'critical':
        return <ExclamationCircleIcon className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />;
      case 'info':
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />;
      default:
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
    }
  };

  const getSeverityBadge = (severity: SEOSeverity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      warning: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      pass: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    };
    return colors[severity] || colors.info;
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 70) return 'text-yellow-500';
    if (score >= 50) return 'text-orange-500';
    return 'text-red-500';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getCategoryIcon = (category: SEOCategory | string) => {
    switch (category) {
      case 'meta_tags':
        return <DocumentTextIcon className="w-5 h-5" />;
      case 'headings':
        return <HashtagIcon className="w-5 h-5" />;
      case 'images':
        return <PhotoIcon className="w-5 h-5" />;
      case 'social':
        return <ShareIcon className="w-5 h-5" />;
      case 'schema':
        return <CodeBracketIcon className="w-5 h-5" />;
      case 'links':
        return <LinkIcon className="w-5 h-5" />;
      case 'content':
        return <ClipboardDocumentListIcon className="w-5 h-5" />;
      case 'technical':
        return <WrenchScrewdriverIcon className="w-5 h-5" />;
      case 'mobile':
        return <DevicePhoneMobileIcon className="w-5 h-5" />;
      case 'performance':
        return <BoltIcon className="w-5 h-5" />;
      default:
        return <EyeIcon className="w-5 h-5" />;
    }
  };

  const toggleIssue = (id: string) => {
    const newExpanded = new Set(expandedIssues);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIssues(newExpanded);
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const totalPages = Math.ceil(totalScans / pageSize);

  // Render Dashboard Tab
  const renderDashboard = () => {
    if (!dashboard) {
      return (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('dashboard.totalScans')}</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{dashboard.totalScans}</p>
              </div>
              <MagnifyingGlassIcon className="w-10 h-10 text-blue-500 opacity-50" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('dashboard.averageScore')}</p>
                <p className={`text-3xl font-bold ${getScoreColor(dashboard.averageScore)}`}>
                  {dashboard.averageScore}
                </p>
              </div>
              <ChartBarIcon className="w-10 h-10 text-green-500 opacity-50" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('overview.criticalIssues')}</p>
                <p className="text-3xl font-bold text-red-500">{dashboard.totalIssues.critical}</p>
              </div>
              <ExclamationCircleIcon className="w-10 h-10 text-red-500 opacity-50" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('overview.warnings')}</p>
                <p className="text-3xl font-bold text-orange-500">{dashboard.totalIssues.warnings}</p>
              </div>
              <ExclamationTriangleIcon className="w-10 h-10 text-orange-500 opacity-50" />
            </div>
          </div>
        </div>

        {/* Recent Scans */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('dashboard.recentScans')}
          </h3>
          {dashboard.recentScans.length > 0 ? (
            <div className="space-y-3">
              {dashboard.recentScans.map((scan) => (
                <div
                  key={scan.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleViewScan(scan.id)}
                >
                  <div className="flex items-center gap-3">
                    <GlobeAltIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs">
                        {scan.url}
                      </p>
                      <p className="text-xs text-gray-500">{formatDate(scan.created_at)}</p>
                    </div>
                  </div>
                  <div className={`text-lg font-bold ${getScoreColor(scan.overall_score)}`}>
                    {scan.overall_score}/100
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">{t('noScans')}</p>
          )}
        </div>
      </div>
    );
  };

  // Render History Tab
  const renderHistory = () => {
    if (loadingHistory) {
      return (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="w-8 h-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">{t('loadingHistory')}</span>
        </div>
      );
    }

    if (scans.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <MagnifyingGlassIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('noScans')}</h3>
          <p className="text-gray-500 mb-4">{t('noScansDesc')}</p>
          <button
            onClick={() => setMainTab('scan')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            {t('newScan')}
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* History Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('score')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('issues')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('duration')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {scans.map((scan) => (
                <tr key={scan.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <GlobeAltIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-white truncate max-w-xs">
                        {scan.url}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-lg font-bold ${getScoreColor(scan.overall_score)}`}>
                      {scan.overall_score}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {scan.critical_issues > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 rounded">
                          {scan.critical_issues} {t('severity.critical')}
                        </span>
                      )}
                      {scan.warning_issues > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 rounded">
                          {scan.warning_issues} {t('severity.warning')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(scan.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {scan.duration_ms ? `${(scan.duration_ms / 1000).toFixed(1)}s` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleViewScan(scan.id)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title={t('viewDetails')}
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteScan(scan.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title={t('delete')}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between bg-white dark:bg-gray-800 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-500">
              {t('showing')} {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalScans)}{' '}
              {t('of')} {totalScans} {t('scans')}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render scan results overview
  const renderOverview = () => {
    if (!result) return null;

    const { score, allIssues, passedChecks, recommendations } = result;
    const criticalCount = allIssues.filter((i) => i.severity === 'critical').length;
    const warningCount = allIssues.filter((i) => i.severity === 'warning').length;
    const infoCount = allIssues.filter((i) => i.severity === 'info').length;

    return (
      <div className="space-y-6">
        {/* Score Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Main Score */}
            <div className="relative">
              <div className="w-32 h-32 relative">
                <svg className="w-32 h-32 transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    strokeWidth="12"
                    fill="none"
                    className="stroke-gray-200 dark:stroke-gray-700"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    strokeWidth="12"
                    fill="none"
                    className={`${getScoreBgColor(score.overall).replace('bg-', 'stroke-')}`}
                    strokeLinecap="round"
                    strokeDasharray={`${(score.overall / 100) * 352} 352`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-3xl font-bold ${getScoreColor(score.overall)}`}>{score.overall}</span>
                  <span className="text-sm text-gray-500">/ 100</span>
                </div>
              </div>
            </div>

            {/* Category Scores */}
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(score.categories).map(([key, value]) => (
                <div key={key} className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className={`text-xl font-bold ${getScoreColor(value)}`}>{value}</div>
                  <div className="text-xs text-gray-500 capitalize">
                    {t(`categories.${key}`, { defaultValue: key.replace(/([A-Z])/g, ' $1').trim() })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Issue Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">{t('overview.criticalIssues')}</span>
              <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
            </div>
            <div className="text-3xl font-bold text-red-500">{criticalCount}</div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">{t('overview.warnings')}</span>
              <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />
            </div>
            <div className="text-3xl font-bold text-orange-500">{warningCount}</div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">{t('overview.info')}</span>
              <InformationCircleIcon className="w-5 h-5 text-blue-500" />
            </div>
            <div className="text-3xl font-bold text-blue-500">{infoCount}</div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">{t('overview.passedChecks')}</span>
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-3xl font-bold text-green-500">{passedChecks.length}</div>
          </div>
        </div>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5" />
              {t('overview.recommendations')}
            </h3>
            <div className="space-y-2">
              {recommendations.map((rec, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <span className="text-blue-500 font-bold">{idx + 1}.</span>
                  <p className="text-gray-700 dark:text-gray-300">{rec}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Passed Checks */}
        {passedChecks.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
              {t('overview.passedChecks')} ({passedChecks.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {passedChecks.map((check, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {check}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMetaAnalysis = () => {
    if (!result) return null;

    const { metaTags, headings } = result;

    return (
      <div className="space-y-6">
        {/* Title & Description */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('meta.titleDescription')}</h3>

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('meta.title')}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    metaTags.title.length >= 30 && metaTags.title.length <= 60
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                  }`}
                >
                  {metaTags.title.length} {t('meta.characters')}
                </span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-gray-900 dark:text-white">
                  {metaTags.title.value || <span className="text-red-500 italic">{t('meta.missing')}</span>}
                </p>
              </div>
              <div className="mt-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    metaTags.title.length >= 30 && metaTags.title.length <= 60
                      ? 'bg-green-500'
                      : metaTags.title.length > 60
                        ? 'bg-orange-500'
                        : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min((metaTags.title.length / 60) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('meta.optimal')}: 30-60 {t('meta.characters')}</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('meta.metaDescription')}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    metaTags.description.length >= 120 && metaTags.description.length <= 160
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                  }`}
                >
                  {metaTags.description.length} {t('meta.characters')}
                </span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-gray-900 dark:text-white">
                  {metaTags.description.value || <span className="text-red-500 italic">{t('meta.missing')}</span>}
                </p>
              </div>
              <div className="mt-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    metaTags.description.length >= 120 && metaTags.description.length <= 160
                      ? 'bg-green-500'
                      : metaTags.description.length > 160
                        ? 'bg-orange-500'
                        : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min((metaTags.description.length / 160) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('meta.optimal')}: 120-160 {t('meta.characters')}</p>
            </div>
          </div>
        </div>

        {/* Other Meta Tags */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('meta.otherMetaTags')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label: t('meta.canonicalUrl'), value: metaTags.canonical },
              { label: t('meta.robots'), value: metaTags.robots },
              { label: t('meta.viewport'), value: metaTags.viewport },
              { label: t('meta.charset'), value: metaTags.charset },
              { label: t('meta.language'), value: metaTags.language },
              { label: t('meta.keywords'), value: metaTags.keywords },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-2">
                {item.value ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <ExclamationCircleIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.label}</span>
                  <p className="text-sm text-gray-500 break-all">
                    {item.value || <span className="italic">{t('meta.notSet')}</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Headings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('meta.headingStructure')}</h3>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
            {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((tag) => (
              <div
                key={tag}
                className={`text-center p-3 rounded-lg ${
                  tag === 'h1' && headings.h1.count !== 1
                    ? 'bg-red-50 dark:bg-red-900/20'
                    : 'bg-gray-50 dark:bg-gray-700'
                }`}
              >
                <div className="text-lg font-bold text-gray-900 dark:text-white uppercase">{tag}</div>
                <div className="text-2xl font-bold">{(headings as any)[tag].count || 0}</div>
              </div>
            ))}
          </div>

          {headings.h1.values.length > 0 && (
            <div className="mt-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('meta.h1Content')}:</span>
              <ul className="mt-2 space-y-1">
                {headings.h1.values.map((h1, idx) => (
                  <li key={idx} className="text-sm text-gray-600 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    {h1}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            {headings.hasProperHierarchy ? (
              <>
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                <span className="text-sm text-green-600">{t('meta.hierarchyProper')}</span>
              </>
            ) : (
              <>
                <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />
                <span className="text-sm text-orange-600">{t('meta.hierarchyIssues')}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContentAnalysis = () => {
    if (!result) return null;

    const { content, images, links } = result;

    return (
      <div className="space-y-6">
        {/* Content Stats */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('content.statistics')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{content.wordCount}</div>
              <div className="text-sm text-gray-500">{t('content.words')}</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{content.readingTime} min</div>
              <div className="text-sm text-gray-500">{t('content.readingTime')}</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{content.textToHtmlRatio}%</div>
              <div className="text-sm text-gray-500">{t('content.textHtmlRatio')}</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{content.paragraphs}</div>
              <div className="text-sm text-gray-500">{t('content.paragraphs')}</div>
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('content.images')} ({images.total})</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{images.withAlt}</div>
              <div className="text-sm text-gray-500">{t('content.withAltText')}</div>
            </div>
            <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{images.withoutAlt}</div>
              <div className="text-sm text-gray-500">{t('content.missingAlt')}</div>
            </div>
            <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{images.withEmptyAlt}</div>
              <div className="text-sm text-gray-500">{t('content.emptyAlt')}</div>
            </div>
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{images.lazyLoaded}</div>
              <div className="text-sm text-gray-500">{t('content.lazyLoaded')}</div>
            </div>
          </div>
        </div>

        {/* Links */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            {t('content.links')} ({links.internal.count + links.external.count})
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{t('content.internalLinks')}</span>
                <span className="text-2xl font-bold text-blue-600">{links.internal.count}</span>
              </div>
              {links.internal.links.length > 0 && (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {links.internal.links.slice(0, 10).map((link, idx) => (
                    <div key={idx} className="text-xs text-gray-500 truncate">
                      {link.href}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{t('content.externalLinks')}</span>
                <span className="text-2xl font-bold text-green-600">{links.external.count}</span>
              </div>
              {links.external.links.length > 0 && (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {links.external.links.slice(0, 10).map((link, idx) => (
                    <div key={idx} className="text-xs text-gray-500 truncate">
                      {link.href}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTechnicalAnalysis = () => {
    if (!result) return null;

    const { technical, mobile, performance } = result;

    return (
      <div className="space-y-6">
        {/* Technical Checks */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('technical.checks')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { label: t('technical.https'), value: technical.hasHttps },
              { label: t('technical.robotsTxt'), value: technical.hasRobotsTxt },
              { label: t('technical.sitemapXml'), value: technical.hasSitemapXml },
              { label: t('technical.compression'), value: !!technical.compression, detail: technical.compression },
              { label: t('technical.viewportMeta'), value: mobile.hasViewportMeta },
              { label: t('technical.mobileOptimized'), value: mobile.hasMobileOptimizedContent },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="font-medium text-gray-700 dark:text-gray-300">{item.label}</span>
                <div className="flex items-center gap-2">
                  {item.detail && <span className="text-sm text-gray-500">{item.detail}</span>}
                  {item.value ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Server Response */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('technical.serverResponse')}</h3>
          <div className="flex items-center gap-4">
            <div className={`text-3xl font-bold ${technical.serverResponseTime < 200 ? 'text-green-500' : technical.serverResponseTime < 500 ? 'text-yellow-500' : 'text-red-500'}`}>
              {technical.serverResponseTime}ms
            </div>
            <div className="flex-1">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${technical.serverResponseTime < 200 ? 'bg-green-500' : technical.serverResponseTime < 500 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.min((technical.serverResponseTime / 1000) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('technical.target')}: &lt;200ms</p>
            </div>
          </div>
        </div>

        {/* Performance Hints */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('technical.performanceHints')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{performance.externalScripts}</div>
              <div className="text-sm text-gray-500">{t('technical.externalScripts')}</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{performance.inlineStyles}</div>
              <div className="text-sm text-gray-500">{t('technical.inlineStyles')}</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{performance.renderBlockingResources.length}</div>
              <div className="text-sm text-gray-500">{t('technical.renderBlocking')}</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{performance.largeImages.length}</div>
              <div className="text-sm text-gray-500">{t('technical.largeImages')}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSocialAnalysis = () => {
    if (!result) return null;

    const { social, schema } = result;

    return (
      <div className="space-y-6">
        {/* Open Graph */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('social.openGraph')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(social.openGraph).map(([key, value]) => (
              <div key={key} className="flex items-start gap-2">
                {value ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <ExclamationCircleIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                )}
                <div className="min-w-0">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">og:{key}</span>
                  <p className="text-sm text-gray-500 break-all">
                    {value || <span className="italic">{t('meta.notSet')}</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Twitter Cards */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('social.twitterCards')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(social.twitter).map(([key, value]) => (
              <div key={key} className="flex items-start gap-2">
                {value ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <ExclamationCircleIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                )}
                <div className="min-w-0">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">twitter:{key}</span>
                  <p className="text-sm text-gray-500 break-all">
                    {value || <span className="italic">{t('meta.notSet')}</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Schema.org */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            {t('social.structuredData')}
          </h3>
          {schema.hasSchema ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                <span className="text-green-600">{t('social.structuredDataFound')}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {schema.schemaTypes.map((type, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 rounded-full text-sm"
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <ExclamationCircleIcon className="w-5 h-5 text-orange-500" />
              <span className="text-orange-600">{t('social.noStructuredData')}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPageSpeed = () => {
    // Use mobile PageSpeed data by default, or fallback to legacy
    const pageSpeed = result?.pageSpeedMobile || result?.pageSpeed;

    if (!pageSpeed) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
          <BoltIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('pageSpeed.notAvailable')}</h3>
          <p className="text-gray-500">{t('pageSpeed.notAvailableDesc')}</p>
        </div>
      );
    }

    const { coreWebVitals, opportunities, resources } = pageSpeed;

    return (
      <div className="space-y-6">
        {/* Performance Score */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="relative">
              <div className="w-32 h-32 relative">
                <svg className="w-32 h-32 transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    strokeWidth="12"
                    fill="none"
                    className="stroke-gray-200 dark:stroke-gray-700"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    strokeWidth="12"
                    fill="none"
                    className={`${getScoreBgColor(pageSpeed.score).replace('bg-', 'stroke-')}`}
                    strokeLinecap="round"
                    strokeDasharray={`${(pageSpeed.score / 100) * 352} 352`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-3xl font-bold ${getScoreColor(pageSpeed.score)}`}>{pageSpeed.score}</span>
                  <span className="text-sm text-gray-500">{t('pageSpeed.performance')}</span>
                </div>
              </div>
            </div>

            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('pageSpeed.coreWebVitals')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { key: 'lcp', label: 'LCP', value: `${coreWebVitals.lcp.value}ms`, rating: coreWebVitals.lcp.rating, target: '<2.5s' },
                  { key: 'fid', label: 'FID', value: `${coreWebVitals.fid.value}ms`, rating: coreWebVitals.fid.rating, target: '<100ms' },
                  { key: 'cls', label: 'CLS', value: coreWebVitals.cls.value.toString(), rating: coreWebVitals.cls.rating, target: '<0.1' },
                  { key: 'fcp', label: 'FCP', value: `${coreWebVitals.fcp.value}ms`, rating: coreWebVitals.fcp.rating, target: '<1.8s' },
                  { key: 'ttfb', label: 'TTFB', value: `${coreWebVitals.ttfb.value}ms`, rating: coreWebVitals.ttfb.rating, target: '<200ms' },
                  { key: 'tti', label: 'TTI', value: `${coreWebVitals.tti.value}ms`, rating: coreWebVitals.tti.rating, target: '<3.8s' },
                ].map((metric) => (
                  <div key={metric.key} className={`p-3 rounded-lg ${getRatingBgColor(metric.rating)}`}>
                    <div className="text-xs text-gray-500 uppercase">{metric.label}</div>
                    <div className={`text-xl font-bold ${getRatingColor(metric.rating)}`}>{metric.value}</div>
                    <div className="text-xs text-gray-400">{t('pageSpeed.target')}: {metric.target}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* More Core Web Vitals */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('pageSpeed.additionalMetrics')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-3 rounded-lg ${getRatingBgColor(coreWebVitals.tbt.rating)}`}>
              <div className="text-xs text-gray-500 uppercase">TBT</div>
              <div className={`text-xl font-bold ${getRatingColor(coreWebVitals.tbt.rating)}`}>{coreWebVitals.tbt.value}ms</div>
              <div className="text-xs text-gray-400">{t('pageSpeed.totalBlockingTime')}</div>
            </div>
            <div className={`p-3 rounded-lg ${getRatingBgColor(coreWebVitals.speedIndex.rating)}`}>
              <div className="text-xs text-gray-500 uppercase">{t('pageSpeed.speedIndex')}</div>
              <div className={`text-xl font-bold ${getRatingColor(coreWebVitals.speedIndex.rating)}`}>{coreWebVitals.speedIndex.value}ms</div>
            </div>
            <div className={`p-3 rounded-lg ${getRatingBgColor(coreWebVitals.inp.rating)}`}>
              <div className="text-xs text-gray-500 uppercase">INP</div>
              <div className={`text-xl font-bold ${getRatingColor(coreWebVitals.inp.rating)}`}>{coreWebVitals.inp.value}ms</div>
              <div className="text-xs text-gray-400">{t('pageSpeed.interactionToNextPaint')}</div>
            </div>
          </div>
        </div>

        {/* Resource Sizes */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('pageSpeed.resourceSizes')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatBytes(resources.totalSize)}</div>
              <div className="text-sm text-gray-500">{t('pageSpeed.total')}</div>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{formatBytes(resources.htmlSize)}</div>
              <div className="text-sm text-gray-500">HTML</div>
            </div>
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">{formatBytes(resources.cssSize)}</div>
              <div className="text-sm text-gray-500">CSS</div>
            </div>
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">{formatBytes(resources.jsSize)}</div>
              <div className="text-sm text-gray-500">JavaScript</div>
            </div>
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{formatBytes(resources.imageSize)}</div>
              <div className="text-sm text-gray-500">{t('pageSpeed.images')}</div>
            </div>
            <div className="p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg text-center">
              <div className="text-2xl font-bold text-pink-600">{formatBytes(resources.fontSize)}</div>
              <div className="text-sm text-gray-500">{t('pageSpeed.fonts')}</div>
            </div>
          </div>
        </div>

        {/* Opportunities */}
        {opportunities.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('pageSpeed.opportunities')}</h3>
            <div className="space-y-3">
              {opportunities.map((opp) => (
                <div key={opp.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900 dark:text-white">{opp.title}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      opp.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                      opp.priority === 'medium' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                      'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}>
                      {opp.priority}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{opp.description}</p>
                  {opp.savings.time && (
                    <p className="text-xs text-green-600 mt-1">{t('pageSpeed.potentialSavings')}: {opp.savings.time}ms</p>
                  )}
                  {opp.savings.bytes && (
                    <p className="text-xs text-green-600 mt-1">{t('pageSpeed.potentialSavings')}: {formatBytes(opp.savings.bytes)}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Combined SEO Analysis with collapsible sections
  const renderSEOAnalysis = () => {
    if (!result) return null;

    const { metaTags, headings, content, images, links, social, schema, technical, mobile } = result;

    const sections = [
      {
        id: 'meta',
        title: t('meta.titleDescription'),
        icon: DocumentTextIcon,
        score: result.score.categories.metaTags,
        content: (
          <div className="space-y-4">
            {/* Title */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('meta.title')}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  metaTags.title.length >= 30 && metaTags.title.length <= 60
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                }`}>
                  {metaTags.title.length} {t('meta.characters')}
                </span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-gray-900 dark:text-white text-sm">
                  {metaTags.title.value || <span className="text-red-500 italic">{t('meta.missing')}</span>}
                </p>
              </div>
            </div>
            {/* Description */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('meta.metaDescription')}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  metaTags.description.length >= 120 && metaTags.description.length <= 160
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                }`}>
                  {metaTags.description.length} {t('meta.characters')}
                </span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-gray-900 dark:text-white text-sm">
                  {metaTags.description.value || <span className="text-red-500 italic">{t('meta.missing')}</span>}
                </p>
              </div>
            </div>
            {/* Other Meta */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                { label: 'Canonical', value: metaTags.canonical },
                { label: 'Robots', value: metaTags.robots },
                { label: 'Viewport', value: metaTags.viewport },
                { label: 'Language', value: metaTags.language },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                  {item.value ? (
                    <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <ExclamationCircleIcon className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  )}
                  <span className="text-gray-600 dark:text-gray-400 truncate">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        id: 'headings',
        title: t('meta.headingStructure'),
        icon: HashtagIcon,
        score: result.score.categories.headings,
        content: (
          <div className="space-y-4">
            <div className="grid grid-cols-6 gap-2">
              {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((tag) => (
                <div
                  key={tag}
                  className={`text-center p-2 rounded-lg ${
                    tag === 'h1' && headings.h1.count !== 1
                      ? 'bg-red-50 dark:bg-red-900/20'
                      : 'bg-gray-50 dark:bg-gray-700'
                  }`}
                >
                  <div className="text-xs font-bold text-gray-500 uppercase">{tag}</div>
                  <div className="text-lg font-bold">{(headings as any)[tag].count || 0}</div>
                </div>
              ))}
            </div>
            {headings.h1.values.length > 0 && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-xs text-gray-500 uppercase">H1:</span>
                <p className="text-sm text-gray-900 dark:text-white">{headings.h1.values[0]}</p>
              </div>
            )}
            <div className="flex items-center gap-2">
              {headings.hasProperHierarchy ? (
                <>
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600">{t('meta.hierarchyProper')}</span>
                </>
              ) : (
                <>
                  <ExclamationTriangleIcon className="w-4 h-4 text-orange-500" />
                  <span className="text-sm text-orange-600">{t('meta.hierarchyIssues')}</span>
                </>
              )}
            </div>
          </div>
        ),
      },
      {
        id: 'content',
        title: t('content.statistics'),
        icon: ClipboardDocumentListIcon,
        score: result.score.categories.content,
        content: (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-xl font-bold text-gray-900 dark:text-white">{content.wordCount}</div>
              <div className="text-xs text-gray-500">{t('content.words')}</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-xl font-bold text-gray-900 dark:text-white">{content.readingTime}m</div>
              <div className="text-xs text-gray-500">{t('content.readingTime')}</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-xl font-bold text-gray-900 dark:text-white">{content.textToHtmlRatio}%</div>
              <div className="text-xs text-gray-500">Text/HTML</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-xl font-bold text-gray-900 dark:text-white">{content.paragraphs}</div>
              <div className="text-xs text-gray-500">{t('content.paragraphs')}</div>
            </div>
          </div>
        ),
      },
      {
        id: 'images',
        title: `${t('content.images')} (${images.total})`,
        icon: PhotoIcon,
        score: result.score.categories.images,
        content: (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-xl font-bold text-green-600">{images.withAlt}</div>
              <div className="text-xs text-gray-500">{t('content.withAltText')}</div>
            </div>
            <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <div className="text-xl font-bold text-red-600">{images.withoutAlt}</div>
              <div className="text-xs text-gray-500">{t('content.missingAlt')}</div>
            </div>
            <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="text-xl font-bold text-orange-600">{images.withEmptyAlt}</div>
              <div className="text-xs text-gray-500">{t('content.emptyAlt')}</div>
            </div>
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-xl font-bold text-blue-600">{images.lazyLoaded}</div>
              <div className="text-xs text-gray-500">{t('content.lazyLoaded')}</div>
            </div>
          </div>
        ),
      },
      {
        id: 'links',
        title: `${t('content.links')} (${links.internal.count + links.external.count})`,
        icon: LinkIcon,
        score: result.score.categories.links,
        content: (
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('content.internalLinks')}</span>
                <span className="text-xl font-bold text-blue-600">{links.internal.count}</span>
              </div>
            </div>
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('content.externalLinks')}</span>
                <span className="text-xl font-bold text-green-600">{links.external.count}</span>
              </div>
            </div>
            {links.broken.length > 0 && (
              <div className="col-span-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-red-600">{t('content.brokenLinks')}</span>
                  <span className="text-xl font-bold text-red-600">{links.broken.length}</span>
                </div>
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'social',
        title: 'Social & Schema',
        icon: ShareIcon,
        score: result.score.categories.social,
        content: (
          <div className="space-y-4">
            {/* Open Graph */}
            <div>
              <span className="text-xs text-gray-500 uppercase">Open Graph</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {['title', 'description', 'image', 'url'].map((key) => (
                  <span
                    key={key}
                    className={`px-2 py-1 rounded text-xs ${
                      (social.openGraph as any)[key]
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    og:{key}
                  </span>
                ))}
              </div>
            </div>
            {/* Twitter */}
            <div>
              <span className="text-xs text-gray-500 uppercase">Twitter Card</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {['card', 'title', 'description', 'image'].map((key) => (
                  <span
                    key={key}
                    className={`px-2 py-1 rounded text-xs ${
                      (social.twitter as any)[key]
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    twitter:{key}
                  </span>
                ))}
              </div>
            </div>
            {/* Schema */}
            <div>
              <span className="text-xs text-gray-500 uppercase">Schema.org</span>
              <div className="mt-1">
                {schema.hasSchema ? (
                  <div className="flex flex-wrap gap-2">
                    {schema.schemaTypes.map((type, idx) => (
                      <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded text-xs">
                        {type}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-orange-600">{t('social.noStructuredData')}</span>
                )}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'technical',
        title: t('technical.checks'),
        icon: WrenchScrewdriverIcon,
        score: result.score.categories.technical,
        content: (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { label: 'HTTPS', value: technical.hasHttps },
              { label: 'robots.txt', value: technical.hasRobotsTxt },
              { label: 'sitemap.xml', value: technical.hasSitemapXml },
              { label: t('technical.compression'), value: !!technical.compression },
              { label: 'Viewport', value: mobile.hasViewportMeta },
              { label: 'Mobile', value: mobile.hasMobileOptimizedContent },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                {item.value ? (
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : (
                  <ExclamationCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
                )}
                <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
              </div>
            ))}
          </div>
        ),
      },
    ];

    return (
      <div className="space-y-3">
        {sections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.has(section.id);

          return (
            <div key={section.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    section.score >= 80 ? 'bg-green-100 dark:bg-green-900/30' :
                    section.score >= 50 ? 'bg-orange-100 dark:bg-orange-900/30' :
                    'bg-red-100 dark:bg-red-900/30'
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      section.score >= 80 ? 'text-green-600' :
                      section.score >= 50 ? 'text-orange-600' :
                      'text-red-600'
                    }`} />
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">{section.title}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-lg font-bold ${getScoreColor(section.score)}`}>{section.score}</span>
                  {isExpanded ? (
                    <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4">
                  {section.content}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Performance tab with Mobile/Desktop toggle
  const renderPerformance = () => {
    // Check if we have PageSpeed data for the selected device
    const currentPageSpeed = pageSpeedDevice === 'mobile'
      ? result?.pageSpeedMobile || result?.pageSpeed
      : result?.pageSpeedDesktop || result?.pageSpeed;

    if (!currentPageSpeed) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
          <BoltIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('pageSpeed.notAvailable')}</h3>
          <p className="text-gray-500">{t('pageSpeed.notAvailableDesc')}</p>
        </div>
      );
    }

    // Check if there was an API error
    if (currentPageSpeed.error) {
      return (
        <div className="space-y-6">
          {/* Device Toggle */}
          <div className="flex justify-center">
            <div className="inline-flex bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
              <button
                onClick={() => setPageSpeedDevice('mobile')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  pageSpeedDevice === 'mobile'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <DevicePhoneMobileIcon className="w-5 h-5" />
                Mobile
              </button>
              <button
                onClick={() => setPageSpeedDevice('desktop')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  pageSpeedDevice === 'desktop'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <GlobeAltIcon className="w-5 h-5" />
                Desktop
              </button>
            </div>
          </div>

          {/* Error Message */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-700 p-6 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-amber-800 dark:text-amber-200 mb-2">
              {t('pageSpeed.errorTitle')}
            </h3>
            <p className="text-amber-700 dark:text-amber-300 mb-4">
              {currentPageSpeed.errorMessage || t('pageSpeed.errorDefault')}
            </p>
            <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 rounded-lg p-4 text-left">
              <p className="font-medium mb-2">{t('pageSpeed.errorHowToFix')}</p>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('pageSpeed.errorApiKey')}</li>
                <li>{t('pageSpeed.errorPublicUrl')}</li>
                <li>{t('pageSpeed.errorQuota')}</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    const { coreWebVitals, opportunities, resources } = currentPageSpeed;

    // Real scores from Google PageSpeed Insights Lighthouse API
    const currentScore = currentPageSpeed.score;

    return (
      <div className="space-y-6">
        {/* Device Toggle */}
        <div className="flex justify-center">
          <div className="inline-flex bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
            <button
              onClick={() => setPageSpeedDevice('mobile')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all ${
                pageSpeedDevice === 'mobile'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <DevicePhoneMobileIcon className="w-5 h-5" />
              Mobile
            </button>
            <button
              onClick={() => setPageSpeedDevice('desktop')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all ${
                pageSpeedDevice === 'desktop'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <GlobeAltIcon className="w-5 h-5" />
              Desktop
            </button>
          </div>
        </div>

        {/* Screenshot Preview */}
        {currentPageSpeed.screenshot && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              {pageSpeedDevice === 'mobile' ? (
                <DevicePhoneMobileIcon className="w-5 h-5 text-purple-500" />
              ) : (
                <GlobeAltIcon className="w-5 h-5 text-blue-500" />
              )}
              {t('pageSpeed.screenshot', 'Page Screenshot')} - {pageSpeedDevice === 'mobile' ? 'Mobile' : 'Desktop'}
            </h3>
            <div className="flex justify-center">
              <div className={`relative overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600 shadow-lg ${
                pageSpeedDevice === 'mobile' ? 'max-w-[280px]' : 'max-w-full'
              }`}>
                <img
                  src={`data:${currentPageSpeed.screenshot.mimeType || 'image/jpeg'};base64,${currentPageSpeed.screenshot.data}`}
                  alt={`${pageSpeedDevice} screenshot`}
                  className="w-full h-auto"
                  style={{ maxHeight: '500px', objectFit: 'contain' }}
                />
                <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  {currentPageSpeed.screenshot.width} x {currentPageSpeed.screenshot.height}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Score Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Score Circle */}
            <div className="relative flex-shrink-0">
              <div className="w-36 h-36 relative">
                <svg className="w-36 h-36 transform -rotate-90">
                  <circle cx="72" cy="72" r="60" strokeWidth="12" fill="none" className="stroke-gray-200 dark:stroke-gray-700" />
                  <circle
                    cx="72"
                    cy="72"
                    r="60"
                    strokeWidth="12"
                    fill="none"
                    className={`${getScoreBgColor(currentScore).replace('bg-', 'stroke-')}`}
                    strokeLinecap="round"
                    strokeDasharray={`${(currentScore / 100) * 377} 377`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-4xl font-bold ${getScoreColor(currentScore)}`}>{currentScore}</span>
                  <span className="text-xs text-gray-500 uppercase">{pageSpeedDevice}</span>
                </div>
              </div>
            </div>

            {/* Core Web Vitals Grid */}
            <div className="flex-1 w-full">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-center sm:text-left">
                Core Web Vitals
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { key: 'lcp', label: 'LCP', value: `${coreWebVitals.lcp.value}ms`, rating: coreWebVitals.lcp.rating },
                  { key: 'fid', label: 'FID', value: `${coreWebVitals.fid.value}ms`, rating: coreWebVitals.fid.rating },
                  { key: 'cls', label: 'CLS', value: coreWebVitals.cls.value.toFixed(3), rating: coreWebVitals.cls.rating },
                  { key: 'fcp', label: 'FCP', value: `${coreWebVitals.fcp.value}ms`, rating: coreWebVitals.fcp.rating },
                  { key: 'ttfb', label: 'TTFB', value: `${coreWebVitals.ttfb.value}ms`, rating: coreWebVitals.ttfb.rating },
                  { key: 'si', label: 'SI', value: `${coreWebVitals.speedIndex.value}ms`, rating: coreWebVitals.speedIndex.rating },
                ].map((metric) => (
                  <div key={metric.key} className={`p-3 rounded-lg ${getRatingBgColor(metric.rating)}`}>
                    <div className="text-xs text-gray-500 font-medium">{metric.label}</div>
                    <div className={`text-lg font-bold ${getRatingColor(metric.rating)}`}>{metric.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Resource Sizes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('pageSpeed.resourceSizes')}</h3>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-900/20 rounded-lg">
              <div className="text-lg font-bold text-gray-600">{formatBytes(resources.totalSize)}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-lg font-bold text-blue-600">{formatBytes(resources.htmlSize)}</div>
              <div className="text-xs text-gray-500">HTML</div>
            </div>
            <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="text-lg font-bold text-purple-600">{formatBytes(resources.cssSize)}</div>
              <div className="text-xs text-gray-500">CSS</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <div className="text-lg font-bold text-yellow-600">{formatBytes(resources.jsSize)}</div>
              <div className="text-xs text-gray-500">JS</div>
            </div>
            <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-lg font-bold text-green-600">{formatBytes(resources.imageSize)}</div>
              <div className="text-xs text-gray-500">Images</div>
            </div>
            <div className="text-center p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
              <div className="text-lg font-bold text-pink-600">{formatBytes(resources.fontSize)}</div>
              <div className="text-xs text-gray-500">Fonts</div>
            </div>
          </div>
        </div>

        {/* Opportunities */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <ArrowTrendingUpIcon className="w-5 h-5 text-green-500" />
            {t('pageSpeed.opportunities')}
            {opportunities.length > 0 && (
              <span className="text-sm font-normal text-gray-500">({opportunities.length})</span>
            )}
          </h3>
          {opportunities.length === 0 ? (
            <p className="text-gray-500 text-sm">{t('pageSpeed.noOpportunities')}</p>
          ) : (
            <div className="space-y-3">
              {opportunities.map((opp, index) => (
                <div
                  key={opp.id}
                  className={`border rounded-lg overflow-hidden ${
                    opp.priority === 'high' ? 'border-red-200 dark:border-red-800' :
                    opp.priority === 'medium' ? 'border-orange-200 dark:border-orange-800' :
                    'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <button
                    onClick={() => toggleSection(`opp-${index}`)}
                    className="w-full p-4 flex items-start justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          opp.priority === 'high' ? 'bg-red-500' :
                          opp.priority === 'medium' ? 'bg-orange-500' :
                          'bg-blue-500'
                        }`} />
                        <span className="font-medium text-gray-900 dark:text-white">{opp.title}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          opp.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                          opp.priority === 'medium' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                          'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}>
                          {t(`pageSpeed.priority${opp.priority.charAt(0).toUpperCase() + opp.priority.slice(1)}`)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        {opp.savings.time && opp.savings.time > 0 && (
                          <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                            <ClockIcon className="w-4 h-4" />
                            -{(opp.savings.time / 1000).toFixed(1)}s
                          </span>
                        )}
                        {opp.savings.bytes && opp.savings.bytes > 0 && (
                          <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                            <ArrowDownIcon className="w-4 h-4" />
                            -{formatBytes(opp.savings.bytes)}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronDownIcon className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                      expandedSections.has(`opp-${index}`) ? 'rotate-180' : ''
                    }`} />
                  </button>
                  {expandedSections.has(`opp-${index}`) && (
                    <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-3 leading-relaxed">
                        {opp.description}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Diagnostics */}
        {currentPageSpeed.diagnostics && currentPageSpeed.diagnostics.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <MagnifyingGlassIcon className="w-5 h-5 text-purple-500" />
              {t('pageSpeed.diagnostics')}
              <span className="text-sm font-normal text-gray-500">({currentPageSpeed.diagnostics.length})</span>
            </h3>
            <div className="space-y-3">
              {currentPageSpeed.diagnostics.map((diag, index) => (
                <div
                  key={diag.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleSection(`diag-${index}`)}
                    className="w-full p-4 flex items-start justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-900 dark:text-white">{diag.title}</span>
                      {diag.details && (
                        <p className="text-sm text-gray-500 mt-1">{diag.details}</p>
                      )}
                    </div>
                    <ChevronDownIcon className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                      expandedSections.has(`diag-${index}`) ? 'rotate-180' : ''
                    }`} />
                  </button>
                  {expandedSections.has(`diag-${index}`) && (
                    <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-3 leading-relaxed">
                        {diag.description}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAllIssues = () => {
    if (!result) return null;

    const groupedIssues = result.allIssues.reduce(
      (acc, issue) => {
        if (!acc[issue.severity]) acc[issue.severity] = [];
        acc[issue.severity].push(issue);
        return acc;
      },
      {} as Record<string, SEOIssue[]>
    );

    const severityOrder: SEOSeverity[] = ['critical', 'warning', 'info'];

    return (
      <div className="space-y-4">
        {severityOrder.map((severity) => {
          const issues = groupedIssues[severity];
          if (!issues || issues.length === 0) return null;

          return (
            <div key={severity}>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                {getSeverityIcon(severity)}
                {t(`severity.${severity}`)} ({issues.length})
              </h3>
              <div className="space-y-2">
                {issues.map((issue) => (
                  <div
                    key={issue.id}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleIssue(issue.id)}
                      className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-750"
                    >
                      <div className="flex items-center gap-3">
                        {getCategoryIcon(issue.category)}
                        <span className="font-medium text-gray-900 dark:text-white">{issue.title}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${getSeverityBadge(severity)}`}>
                          {t(`severity.${severity}`)}
                        </span>
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          {t(`categories.${issue.category}`, { defaultValue: issue.category })}
                        </span>
                      </div>
                      {expandedIssues.has(issue.id) ? (
                        <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </button>

                    {expandedIssues.has(issue.id) && (
                      <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700 pt-3 space-y-3">
                        <div>
                          <span className="text-sm font-medium text-gray-500">{t('issueDetails.description')}</span>
                          <p className="text-gray-700 dark:text-gray-300">{issue.description}</p>
                        </div>

                        <div>
                          <span className="text-sm font-medium text-gray-500">{t('issueDetails.impact')}</span>
                          <p className="text-gray-700 dark:text-gray-300">{issue.impact}</p>
                        </div>

                        <div>
                          <span className="text-sm font-medium text-gray-500">{t('issueDetails.howToFix')}</span>
                          <p className="text-gray-700 dark:text-gray-300">{issue.howToFix}</p>
                        </div>

                        {issue.currentValue && (
                          <div className="flex gap-4">
                            <div>
                              <span className="text-sm font-medium text-gray-500">{t('issueDetails.current')}</span>
                              <p className="text-sm text-red-600">{issue.currentValue}</p>
                            </div>
                            {issue.recommendedValue && (
                              <div>
                                <span className="text-sm font-medium text-gray-500">{t('issueDetails.recommended')}</span>
                                <p className="text-sm text-green-600">{issue.recommendedValue}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {result.allIssues.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-2" />
            <p>No issues found!</p>
          </div>
        )}
      </div>
    );
  };

  // Render Scan Tab
  const renderScanTab = () => {
    return (
      <div className="space-y-6">
        {/* URL Input */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('enterUrl')}</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('websiteUrl')}
              </label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <GlobeAltIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && handleScan()}
                  />
                </div>
                <button
                  onClick={handleScan}
                  disabled={scanning || !url}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 font-medium"
                >
                  {scanning ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      {t('scanning')}
                    </>
                  ) : (
                    <>
                      <PlayIcon className="w-5 h-5" />
                      {t('scan')}
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={checkRobots}
                  onChange={(e) => setCheckRobots(e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('options.checkRobots')}</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={checkSitemap}
                  onChange={(e) => setCheckSitemap(e.target.checked)}
                  className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('options.checkSitemap')}</span>
              </label>
            </div>
          </div>
        </div>

        {/* Results Section */}
        {result ? (
          <>
            {/* Scanned URL Info - Modern Card */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-5 rounded-2xl shadow-lg shadow-blue-500/20 mb-6">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-xl">
                    <GlobeAltIcon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-blue-100 text-sm font-medium">{t('scannedUrl')}</p>
                    <p className="text-white font-semibold text-lg truncate max-w-md">{result.url}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-blue-100 text-xs uppercase tracking-wide">{t('score')}</p>
                      <p className="text-white font-bold text-2xl">{result.score.overall}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-blue-100 text-xs uppercase tracking-wide">{t('duration')}</p>
                      <p className="text-white font-bold text-2xl">{(result.duration / 1000).toFixed(1)}s</p>
                    </div>
                    <div className="text-center">
                      <p className="text-blue-100 text-xs uppercase tracking-wide">{t('issues')}</p>
                      <p className="text-white font-bold text-2xl">{result.allIssues.length}</p>
                    </div>
                  </div>
                  <div className="h-12 w-px bg-white/20 hidden lg:block" />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleExport('pdf')}
                      disabled={exporting !== null}
                      className="px-4 py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl disabled:opacity-50 flex items-center gap-2 text-sm font-medium transition-colors backdrop-blur-sm"
                    >
                      {exporting === 'pdf' ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : (
                        <DocumentArrowDownIcon className="w-4 h-4" />
                      )}
                      PDF
                    </button>
                    <button
                      onClick={() => handleExport('excel')}
                      disabled={exporting !== null}
                      className="px-4 py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl disabled:opacity-50 flex items-center gap-2 text-sm font-medium transition-colors backdrop-blur-sm"
                    >
                      {exporting === 'excel' ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : (
                        <DocumentArrowDownIcon className="w-4 h-4" />
                      )}
                      Excel
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Result Sub-tabs - Simplified 4 tabs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
              {[
                {
                  id: 'overview',
                  label: t('tabs.overview'),
                  icon: ChartBarIcon,
                  color: 'blue',
                  value: result.score.overall,
                  suffix: '/100'
                },
                {
                  id: 'seo',
                  label: 'SEO',
                  icon: MagnifyingGlassIcon,
                  color: 'purple',
                  value: Math.round((result.score.categories.metaTags + result.score.categories.headings + result.score.categories.content + result.score.categories.links) / 4),
                  suffix: '/100'
                },
                {
                  id: 'performance',
                  label: t('tabs.pageSpeed'),
                  icon: BoltIcon,
                  color: 'green',
                  value: (() => {
                    const ps = result.pageSpeedMobile || result.pageSpeed;
                    if (!ps) return '-';
                    if (ps.error) return '!';
                    return Math.round(ps.score);
                  })(),
                  suffix: (() => {
                    const ps = result.pageSpeedMobile || result.pageSpeed;
                    if (!ps || ps.error) return '';
                    return '/100';
                  })()
                },
                {
                  id: 'issues',
                  label: t('tabs.allIssues'),
                  icon: ExclamationTriangleIcon,
                  color: 'red',
                  value: result.allIssues.length,
                  suffix: ''
                },
              ].map((tab) => {
                const Icon = tab.icon;
                const isActive = resultTab === tab.id;
                const criticalCount = tab.id === 'issues' ? result.allIssues.filter(i => i.severity === 'critical').length : 0;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setResultTab(tab.id as ResultTab)}
                    className={`relative flex flex-col items-center p-4 rounded-xl font-medium transition-all duration-200 ${
                      isActive
                        ? tab.color === 'blue' ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500 shadow-lg shadow-blue-500/10' :
                          tab.color === 'purple' ? 'bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-500 shadow-lg shadow-purple-500/10' :
                          tab.color === 'green' ? 'bg-green-50 dark:bg-green-900/20 border-2 border-green-500 shadow-lg shadow-green-500/10' :
                          'bg-red-50 dark:bg-red-900/20 border-2 border-red-500 shadow-lg shadow-red-500/10'
                        : 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <Icon className={`w-6 h-6 mb-2 ${
                      isActive
                        ? tab.color === 'blue' ? 'text-blue-500' :
                          tab.color === 'purple' ? 'text-purple-500' :
                          tab.color === 'green' ? 'text-green-500' :
                          'text-red-500'
                        : 'text-gray-400'
                    }`} />
                    <span className={`text-xs uppercase tracking-wide mb-1 ${isActive ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500'}`}>
                      {tab.label}
                    </span>
                    <div className="flex items-baseline gap-0.5">
                      <span className={`text-2xl font-bold ${
                        tab.id === 'issues' && criticalCount > 0 ? 'text-red-500' :
                        isActive
                          ? tab.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                            tab.color === 'purple' ? 'text-purple-600 dark:text-purple-400' :
                            tab.color === 'green' ? 'text-green-600 dark:text-green-400' :
                            'text-red-600 dark:text-red-400'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {tab.value}
                      </span>
                      <span className="text-xs text-gray-400">{tab.suffix}</span>
                    </div>
                    {tab.id === 'issues' && criticalCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                        {criticalCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {resultTab === 'overview' && renderOverview()}
            {resultTab === 'seo' && renderSEOAnalysis()}
            {resultTab === 'performance' && renderPerformance()}
            {resultTab === 'issues' && renderAllIssues()}
          </>
        ) : (
          /* Info Cards when no result */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <DocumentTextIcon className="w-8 h-8 text-blue-500 mb-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('info.metaTagsTitle')}</h3>
              <p className="text-sm text-gray-500">{t('info.metaTagsDesc')}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <ShareIcon className="w-8 h-8 text-green-500 mb-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('info.socialTagsTitle')}</h3>
              <p className="text-sm text-gray-500">{t('info.socialTagsDesc')}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <WrenchScrewdriverIcon className="w-8 h-8 text-purple-500 mb-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('info.technicalSeoTitle')}</h3>
              <p className="text-sm text-gray-500">{t('info.technicalSeoDesc')}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <MagnifyingGlassIcon className="w-8 h-8 text-blue-500" />
            {t('title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('subtitle')}
          </p>
        </div>

        {/* Main Tabs - Modern Card Style */}
        <div className="flex gap-3 mb-6">
          {[
            { id: 'scan', icon: MagnifyingGlassIcon, label: t('scanUrl'), color: 'blue' },
            { id: 'history', icon: ClockIcon, label: t('history'), color: 'purple' },
            { id: 'dashboard', icon: ChartBarIcon, label: 'Dashboard', color: 'green' },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = mainTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setMainTab(tab.id as MainTab)}
                className={`group relative flex items-center gap-3 px-5 py-3 rounded-xl font-medium transition-all duration-200 ${
                  isActive
                    ? tab.color === 'blue'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                      : tab.color === 'purple'
                        ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                        : 'bg-green-600 text-white shadow-lg shadow-green-500/30'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <Icon className={`w-5 h-5 transition-transform group-hover:scale-110 ${isActive ? 'text-white' : ''}`} />
                <span>{tab.label}</span>
                {isActive && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-current rounded-full opacity-0" />
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {mainTab === 'scan' && renderScanTab()}
        {mainTab === 'history' && renderHistory()}
        {mainTab === 'dashboard' && renderDashboard()}
      </div>
    </MainLayout>
  );
}
